import { Button, Input } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { AxisValueDTO } from '@/services/classification.service'

/**
 * A single editable value: view mode shows label/code + edit/retire actions;
 * edit mode shows inline Label + Status fields. Code is immutable. Retire is
 * confirmed by the owning page's ConfirmDialog, so this row stays pure.
 * Used by the flat-axis list.
 */
export interface ValueRowActions {
	onSave: (valueId: string, body: { label?: string; status?: string }) => void
	onRetire: (valueId: string) => void
	/** Optional: absent where merging is not offered, so the row shows no dead control. */
	onMerge?: (valueId: string) => void
	saving?: boolean
}

const STATUSES = ['active', 'retired']

export function ValueRow({ value, onSave, onRetire, onMerge, saving }: { value: AxisValueDTO } & ValueRowActions) {
	const [editing, setEditing] = useState(false)
	const [label, setLabel] = useState(value.label)
	const [status, setStatus] = useState(value.status)
	const retired = value.status === 'retired' || value.validTo !== null

	if (editing) {
		return (
			<div className="flex flex-wrap items-center gap-2 min-w-0">
				<Input className="w-full" value={label} onChange={(e) => setLabel(e.target.value)} />
				<select
					className="border rounded-ds-base px-2 py-1 text-sm"
					value={status}
					onChange={(e) => setStatus(e.target.value)}
				>
					{STATUSES.map((s) => (
						<option key={s}>{s}</option>
					))}
				</select>
				<Button
					onPress={() => {
						onSave(value.valueId, { label, status })
						setEditing(false)
					}}
					isDisabled={saving}
				>
					Save
				</Button>
				<button type="button" className="text-xs text-ds-text-muted hover:underline" onClick={() => setEditing(false)}>
					cancel
				</button>
			</div>
		)
	}

	return (
		<div className="min-w-0">
			<div className="truncate">
				<span className={`text-sm ${retired ? 'text-ds-text-subtle line-through' : 'font-medium'}`}>{value.label}</span>
				<span className="ml-1 text-xs text-ds-text-subtle">{value.code}</span>
			</div>
			<div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
				<Link className="text-ds-primary hover:underline" to={`/admin/classification/values/${value.valueId}/mappings`}>
					mappings
				</Link>
				<Link
					className="text-ds-primary hover:underline"
					to={`/admin/classification/values/${value.valueId}/availability`}
				>
					availability
				</Link>
				<button type="button" className="text-ds-primary hover:underline" onClick={() => setEditing(true)}>
					edit
				</button>
				{!retired && onMerge && (
					<button type="button" className="text-ds-primary hover:underline" onClick={() => onMerge(value.valueId)}>
						merge
					</button>
				)}
				{!retired && (
					<button type="button" className="text-ds-danger hover:underline" onClick={() => onRetire(value.valueId)}>
						retire
					</button>
				)}
			</div>
		</div>
	)
}
